import { useEffect } from 'react'

import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import { fetchOfferById } from 'actions/OfferActions'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL } from 'constants/offer'
import useOffer from 'hooks/Offers/useOffer'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'

export interface DrawerFlashAccommodationDetailsSource {
  kind: 'flash'
  offer?: App.HotelOffer
  loading: boolean
  error?: unknown
}

export interface DrawerBedbankAccommodationDetailsSource {
  kind: 'bedbank'
  offer?: App.BedbankOffer
  loading: boolean
  error?: unknown
}

export interface DrawerPropertyAccommodationDetailsSource {
  kind: 'property'
  offer?: App.HotelOffer
  property?: App.Property.Property
  mapping?: App.PropertyOfferMapping
  loading: boolean
  error?: unknown
}

interface DrawerUnsupportedAccommodationDetailsSource {
  kind: 'unsupported'
  loading: false
}

export type DrawerAccommodationDetailsSource =
  | DrawerFlashAccommodationDetailsSource
  | DrawerBedbankAccommodationDetailsSource
  | DrawerPropertyAccommodationDetailsSource
  | DrawerUnsupportedAccommodationDetailsSource

function useDrawerFlashOffer(offerId: string, disabled: boolean) {
  const dispatch = useAppDispatch()
  const offer = useAppSelector((state) => state.offer.offers[offerId]) as App.HotelOffer | undefined
  const loading = useAppSelector((state) => !!state.offer.offersLoading[offerId])
  const error = useAppSelector((state) => state.offer.offerErrors[offerId])

  useEffect(() => {
    if (!disabled && !offer && !loading && !error) {
      dispatch(fetchOfferById(offerId))
    }
  }, [disabled, dispatch, error, loading, offer, offerId])

  if (disabled) return [undefined, false, undefined] as const
  return [offer, loading || (!offer && !error), error] as const
}

function useDrawerBedbankOffer(offerId: string, disabled: boolean) {
  const dispatch = useAppDispatch()
  const offer = useAppSelector((state) => state.offer.bedbankOffers[offerId]) as App.BedbankOffer | undefined
  const loading = useAppSelector((state) => !!state.offer.offersLoading[offerId])
  const error = useAppSelector((state) => state.offer.offerErrors[offerId])

  useEffect(() => {
    if (!disabled && !offer && !loading && !error) {
      dispatch(fetchBedbankOfferById(offerId))
    }
  }, [disabled, dispatch, error, loading, offer, offerId])

  if (disabled) return [undefined, false, undefined] as const
  return [offer, loading || (!offer && !error), error] as const
}

function useDrawerAccommodationDetailsSource(
  offerId: string,
  offerType: string | undefined,
  disabled = false,
): DrawerAccommodationDetailsSource {
  const isFlash = !disabled && offerType === OFFER_TYPE_HOTEL
  const isBedbank = !disabled && offerType === OFFER_TYPE_BED_BANK
  const isProperty = !disabled && offerType === OFFER_TYPE_ALWAYS_ON

  const [flashOffer, flashLoading, flashError] = useDrawerFlashOffer(offerId, !isFlash)
  const [bedbankOffer, bedbankLoading, bedbankError] = useDrawerBedbankOffer(offerId, !isBedbank)
  const [mapping, mappingLoading, mappingError] = usePropertyOfferMapping(
    OFFER_TYPE_ALWAYS_ON,
    offerId,
    { disabled: !isProperty },
  )
  const [property, propertyLoading, propertyError] = useProperty(mapping?.propertyId, {
    disabled: !isProperty || !mapping,
  })
  const [propertyOffer, propertyOfferLoading, propertyOfferError] = useOffer<App.HotelOffer>(offerId, {
    disabled: !isProperty || !mapping,
  })

  if (isFlash) {
    return { kind: 'flash', offer: flashOffer, loading: flashLoading, error: flashError }
  }

  if (isBedbank) {
    return { kind: 'bedbank', offer: bedbankOffer, loading: bedbankLoading, error: bedbankError }
  }

  if (isProperty) {
    return {
      kind: 'property',
      offer: propertyOffer,
      property,
      mapping,
      loading: mappingLoading || propertyLoading || propertyOfferLoading,
      error: mappingError ?? (mapping ? propertyError ?? propertyOfferError : undefined),
    }
  }

  return { kind: 'unsupported', loading: false }
}

export default useDrawerAccommodationDetailsSource
